<template>
  <div :class="b()" :data-name="location.name">
    <h3 :class="b('info-window-title')">
      {{ location.displayName }}
    </h3>
    <button
      v-if="sessionStore.flags.storeSelectionEnabled"
      :aria-label="
        $t(location.favorite ? 'c-google-maps-info-window.removeAsFavorite' : 'c-google-maps-info-window.setAsFavorite')
      "
      :class="b('info-window-action-button')"
      type="button"
      @click="$emit('click', location)"
    >
      <e-icon :icon="location.favorite ? 'i-close' : 'i-pin'" :size="location.favorite ? '18' : '22'" />
    </button>
    <a
      v-if="directionsLink"
      :class="b('info-window-direction-link')"
      :href="directionsLink"
      target="_blank"
      rel="noopener noreferrer"
    >
      <e-icon icon="i-google-direction" />
    </a>
    <div v-if="location.address" :class="b('info-window-address')">
      <div>{{ location.address.line1 }} {{ location.address.line2 }}</div>
      <div>
        {{ location.address.country?.isocode }}-{{ location.address.postalCode }} {{ location.address.town }}
      </div>
    </div>
    <div v-if="showOpeningTimes && location.todayOpeningTime && !location.todayOpeningTime?.closed"
         :class="b('info-window-opening-hours')"
    >
      <div>{{ $t('global.openToday') }}:</div>
      <div>{{ getFormattedOpeningHours(location.todayOpeningTime) }}</div>
    </div>
  </div>
</template>

<script lang="ts">
  import {
    defineComponent,
    PropType,
  } from 'vue';
  import useSessionStore from '@/stores/session';
  import eIcon from '@/elements/e-icon.vue';
  import { PointOfService } from '@/types/point-of-service';
  import getFormattedOpeningHours from '@/helpers/opening-hours';

  type Setup = {
    getFormattedOpeningHours: typeof getFormattedOpeningHours;
    sessionStore: ReturnType<typeof useSessionStore>;
  }

  /**
   * Renders a Google Map.
   */
  export default defineComponent({
    name: 'c-google-maps-info-window',
    components: { eIcon },

    // components: {},

    props: {
      /**
       * Expects a list of locations (markers). You can define additional properties if required.
       *
       * lat/lng OR geocode MUST be defined!
       */
      location: {
        type: Object as PropType<PointOfService>,
        required: true,
      },
    },
    emits: {
      click: (payload: PointOfService) => !!payload,
    },

    setup(): Setup {
      return {
        getFormattedOpeningHours,
        sessionStore: useSessionStore(),
      };
    },

    // data(): Data {
    //   return {};
    // },

    computed: {
      directionsLink(): string | undefined {
        const { latitude, longitude } = this.location.geoPoint || {};

        if (latitude && longitude) {
          return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
        }

        return undefined;
      },

      showOpeningTimes(): boolean {
        return import.meta.env.VITE_STORE_FINDER_SHOW_OPENING_TIMES === '1';
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    // methods: {},
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .c-google-maps-info-window {
    @include mixins.font(variables.$font-size--14);

    display: grid;
    grid-gap: variables.$spacing--10 variables.$spacing--15;
    grid-template-areas:
      'title actionButton directionLink'
      'address address address'
      'openingHours openingHours openingHours';
    grid-template-columns: 1fr auto auto;
    padding: variables.$spacing--5 variables.$spacing--10;

    &__info-window-title {
      @include mixins.font(variables.$font-size--18);

      grid-area: title;
      margin-bottom: 0;
    }

    &__info-window-action-button {
      display: flex;
      grid-area: actionButton;
      align-items: center;
      cursor: pointer;
      color: variables.$color-primary--1;
    }

    &__info-window-direction-link {
      display: flex;
      grid-area: directionLink;
      align-items: center;
    }

    &__info-window-address {
      grid-area: address;
    }

    &__info-window-opening-hours {
      grid-area: openingHours;
    }
  }
</style>
